import { useEffect } from 'react'
import { PRODUCT_NAME } from '../lib/product'

// Last pathname a screen announced from — module state so it survives the
// screen swap that IS the navigation.
let lastPath: string | null = null

/** Test-only reset so specs can exercise the first-load branch. */
export function resetPageTitleTracking() {
  lastPath = null
}

/**
 * SPA route announcement (WCAG 2.4.2 Page Titled + 2.4.3 Focus Order): every
 * screen names the document, and on route CHANGES — never the initial load —
 * focus moves to the new screen's h1 (fallback: the main region) so keyboard
 * and screen-reader users land on the new content instead of a stale, unmounted
 * trigger. Inert on the showcase board, where the pathname never changes.
 *
 * `productName` defaults to the golf-club brand; the frozen steam-era screens
 * under /steam pass LEGACY_PRODUCT_NAME so their titles keep saying Fireside.
 */
export function usePageTitle(title?: string, productName: string = PRODUCT_NAME) {
  useEffect(() => {
    document.title = title ? `${title} · ${productName}` : productName
  }, [title, productName])

  useEffect(() => {
    const path = window.location.pathname
    if (lastPath !== null && lastPath !== path) {
      const target =
        document.querySelector<HTMLElement>('main h1') ?? document.querySelector<HTMLElement>('main')
      if (target) {
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1')
        target.focus()
      }
    }
    lastPath = path
    // Runs once per screen mount: focus tracks navigation, not re-renders.
  }, [])
}
